import { useCallback, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";

import { useOutletApi } from "@/features/authentication";
import { formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomAsyncAutocomplete,
  ProductVariantNameCell,
  useSnackbar,
} from "@/features/ui";
import { ReturnableTypeEnum } from "../../returnable-type";
import { PurchaseReturnStatusEnum } from "@/features/purchase-invoices";

export const SelectPurchaseInvoiceProductVariantSection = () => {
  const { control, setValue } = useFormContext();
  const { showSnackbar } = useSnackbar();
  const apiRef = useGridApiRef();
  const [purchaseInvoice, setPurchaseInvoice] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productVariants, setProductVariants] = useState([]);
  const [loadingProductVariants, setLoadingProductVariants] = useState(false);
  const [
    selectedPurchaseInvoiceProductVariants,
    setSelectedPurchaseInvoiceProductVariants,
  ] = useState([]);

  const { append } = useFieldArray({
    control,
    name: "returnableProductVariants",
  });

  const [
    seller,
    buyer,
    returnableProductVariants,
    purchaseReturnExpenses,
    returnableId,
  ] = useWatch({
    control,
    name: [
      "seller",
      "buyer",
      "returnableProductVariants",
      "purchaseReturnExpenses",
      "returnableId",
    ],
    exact: true,
  });

  const outletApi = useOutletApi(buyer?.id);

  // Disable if seller not selected OR if items exist from different source.
  // Attached expenses pin the source just as returned goods do.
  const disableBrowsePurchaseInvoice =
    !seller ||
    ((returnableProductVariants?.length > 0 ||
      purchaseReturnExpenses?.length > 0) &&
      returnableId !== null);

  const fetchPurchaseInvoices = useCallback(
    async (searchValue, { page, size } = {}) => {
      if (!seller || !buyer) return [];

      const returnableStatuses = [
        PurchaseReturnStatusEnum.NONE,
        PurchaseReturnStatusEnum.PARTIALLY_RETURNED,
      ];

      const {
        data: { data },
      } = await outletApi.get("purchase-invoices", {
        params: {
          page,
          size,
          sellerableId: seller?.id,
          referenceNumber: searchValue || null,
          returnStatuses: returnableStatuses,
          excludeReturned: true,
          hasOutstanding: true,
        },
      });
      return data;
    },
    [buyer, seller, outletApi]
  );

  const fetchPurchaseInvoiceProductVariants = useCallback(
    async (referenceNumber) => {
      if (!referenceNumber || !buyer) return;

      setLoadingProductVariants(true);

      try {
        const {
          data: { data },
        } = await outletApi.get(
          `purchase-invoices/${referenceNumber}/purchase-invoice-product-variants`,
          {
            params: {
              withGoodsReceipt: true,
            },
          }
        );

        // Adjust remaining quantity based on existing return items
        const adjustedProductVariants = data
          .map((purchaseInvoiceProductVariant) => {
            const totalReturnedQuantity = returnableProductVariants
              .filter(
                (returnProductVariant) =>
                  returnProductVariant.returnableProductVariantId ===
                  purchaseInvoiceProductVariant.id
              )
              .reduce((total, variant) => total + variant.quantity, 0);

            return {
              ...purchaseInvoiceProductVariant,
              adjustedRemainingQuantity:
                purchaseInvoiceProductVariant.quantity - totalReturnedQuantity,
            };
          })
          .filter((variant) => variant.adjustedRemainingQuantity > 0);

        setProductVariants(adjustedProductVariants);
      } catch (error) {
        showSnackbar(getErrorMessage(error, "Gagal memuat rincian faktur"), "error");
      } finally {
        setLoadingProductVariants(false);
      }
    },
    [buyer, returnableProductVariants, outletApi, showSnackbar]
  );

  const handlePurchaseInvoiceChange = (newValue) => {
    setPurchaseInvoice(newValue);
    if (newValue) {
      // Record the source as soon as the invoice is picked rather than when
      // goods are confirmed: the Biaya tab needs its reference number to list
      // the invoice's expense lines.
      setValue("returnableId", newValue.id);
      setValue("returnableType", ReturnableTypeEnum.PURCHASE_INVOICE);
      setValue("returnableReferenceNumber", newValue.referenceNumber);
      fetchPurchaseInvoiceProductVariants(newValue.referenceNumber);
      setDialogOpen(true);
    } else {
      // Only reachable while nothing is attached — the field locks otherwise.
      setValue("returnableId", null);
      setValue("returnableReferenceNumber", null);
      setProductVariants([]);
    }
    setSelectedPurchaseInvoiceProductVariants([]);
  };

  const columns = [
    {
      field: "name",
      headerName: "Nama Barang",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell name={name} sku={sku} />
      ),
    },
    {
      field: "adjustedRemainingQuantity",
      headerName: "Kuantitas",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Satuan",
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
  ];

  const submitSelection = () => {
    // Check if there's already a returnable source set
    if (returnableId && returnableId !== purchaseInvoice.id) {
      showSnackbar(
        "Anda hanya dapat memilih produk dari satu sumber dokumen. Hapus produk yang ada terlebih dahulu.",
        "error"
      );
      return;
    }

    // Set the returnable source if not already set
    if (!returnableId) {
      setValue("returnableId", purchaseInvoice.id);
      setValue("returnableType", ReturnableTypeEnum.PURCHASE_INVOICE);
    }

    selectedPurchaseInvoiceProductVariants.forEach(
      ({
        id: purchaseInvoiceProductVariantId,
        goodsReceipt: { warehouseId },
        name,
        sku,
        adjustedRemainingQuantity,
        product: {
          unitOfMeasurement: { name: unitOfMeasurement },
        },
        price,
        discount,
      }) => {
        const alreadyExists = returnableProductVariants.some(
          (variant) =>
            variant.returnableProductVariantId ===
            purchaseInvoiceProductVariantId
        );

        if (!alreadyExists) {
          append({
            id: ulid(),
            returnableProductVariantId: purchaseInvoiceProductVariantId,
            warehouseId,
            name,
            sku,
            quantity: adjustedRemainingQuantity,
            unitOfMeasurement,
            price: price ?? 0,
            discount: discount ?? 0,
            note: null,
          });
        }
      }
    );

    // Close dialog
    setDialogOpen(false);
    setProductVariants([]);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setProductVariants([]);
  };

  return (
    <>
      <CustomAsyncAutocomplete
        value={purchaseInvoice}
        onChange={handlePurchaseInvoiceChange}
        disabled={disableBrowsePurchaseInvoice}
        fetchOptions={fetchPurchaseInvoices}
        getOptionLabel={(option) => option.referenceNumber}
        isOptionEqualToValue={(option, selectedOption) =>
          option.id === selectedOption.id
        }
        renderOption={(props, option) => {
          const { id, referenceNumber, transactionDate } = option;
          return (
            <li {...props} key={id}>
              <div>
                <div>{referenceNumber}</div>
                <div style={{ fontSize: "0.75rem" }}>
                  {formatDate(transactionDate)}
                </div>
              </div>
            </li>
          );
        }}
        TextFieldProps={{
          helperText: disableBrowsePurchaseInvoice
            ? !seller
              ? "Pilih Pemasok terlebih dahulu"
              : "Tidak dapat mengubah sumber dokumen setelah menambah produk"
            : undefined,
          InputProps: {
            placeholder: "Cari No. Referensi Faktur Pembelian",
          },
        }}
      />

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Rincian Barang
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DataGrid
            getRowHeight={() => "auto"}
            apiRef={apiRef}
            columns={columns}
            rows={productVariants}
            loading={loadingProductVariants}
            checkboxSelection
            onRowSelectionModelChange={() => {
              const selectedModels = Array.from(
                gridRowSelectionIdsSelector(apiRef.current.state).values()
              );
              setSelectedPurchaseInvoiceProductVariants(selectedModels);
            }}
            sx={{ minHeight: 400 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            fullWidth
            variant="contained"
            disabled={selectedPurchaseInvoiceProductVariants.length === 0}
            onClick={submitSelection}
          >
            Lanjut
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
